import React, { useEffect, useState } from 'react'
import { filesize } from 'filesize'
import { BackendRemote, onDCEvent } from '../../../backend-com'
import { C, T } from '@deltachat/jsonrpc-client'
import { selectedAccountId } from '../../../ScreenController'

import styles from './styles.module.scss'
import { Avatar } from '../../Avatar'
import moment from 'moment'
import useTranslationFunction from '../../../hooks/useTranslationFunction'
import { useRpcFetch } from '../../../hooks/useFetch'
import useOpenViewProfileDialog from '../../../hooks/dialog/useOpenViewProfileDialog'
import { isImage } from '../../attachment/Attachment'

/** Format a millisecond duration as `m:ss` (or `h:mm:ss`). */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000)
  const seconds = totalSeconds % 60
  const minutes = Math.floor(totalSeconds / 60) % 60
  const hours = Math.floor(totalSeconds / 3600)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`
}

function useMessageReadReceipts(messageId: number) {
  const accountId = selectedAccountId()
  const receiptsFetch = useRpcFetch(BackendRemote.rpc.getMessageReadReceipts, [
    accountId,
    messageId,
  ])
  const refresh = receiptsFetch.refresh

  useEffect(
    () =>
      onDCEvent(accountId, 'MsgRead', ({ msgId }) => {
        if (msgId === messageId) {
          refresh()
        }
      }),
    [accountId, messageId, refresh]
  )

  return receiptsFetch
}

type BasicMessageInfoProps = {
  message: T.Message
  info: string
  messageId: number
  onClose: () => void
}

export function BasicMessageInfo(props: BasicMessageInfoProps) {
  const { message } = props
  const tx = useTranslationFunction()
  const receiptsFetch = useMessageReadReceipts(props.messageId)

  const receipts = receiptsFetch.lingeringResult?.ok
    ? receiptsFetch.lingeringResult.value
    : []

  // Format sent timestamp
  const sentTimestamp = moment(message.timestamp * 1000)
  const sentFormatted = sentTimestamp.format('lll')

  // Format received timestamp
  const receivedTimestamp = message.receivedTimestamp
    ? moment(message.receivedTimestamp * 1000)
    : null
  const receivedFormatted = receivedTimestamp
    ? receivedTimestamp.format('lll')
    : null

  // A message counts as failed once the core marks it OUT_FAILED. We show the
  // error text prominently since this dialog is where the failed-status icon
  // in the message bubble now sends the user.
  const failed = message.state === C.DC_STATE_OUT_FAILED
  const errorText = message.error ?? undefined

  // Attachment metadata. `file` is the blob path; the rest is only meaningful
  // when there is an attachment, so we gate the whole section on it.
  const hasAttachment = Boolean(message.file)
  const hasDimensions =
    message.dimensionsWidth > 0 && message.dimensionsHeight > 0
  const showDimensions =
    hasDimensions && (isImage(message.viewType) || message.viewType === 'Video')
  const showDuration =
    message.duration > 0 &&
    (message.viewType === 'Audio' ||
      message.viewType === 'Voice' ||
      message.viewType === 'Video')

  return (
    <div className={styles.formattedMessageInfo}>
      {(failed || errorText) && (
        <div className={styles.errorRow} role='status'>
          <span className={styles.errorIcon} aria-hidden='true' />
          <span className={styles.errorText}>
            {errorText ? tx('error_x', errorText) : tx('error')}
          </span>
        </div>
      )}
      <div className={styles.infoRow}>
        <span className={styles.infoLabel}>
          {tx('message_detail_sent_desktop')}
        </span>
        <span className={styles.infoValue}>{sentFormatted}</span>
      </div>
      {receivedFormatted && (
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>
            {tx('message_detail_received_desktop')}
          </span>
          <span className={styles.infoValue}>{receivedFormatted}</span>
        </div>
      )}
      {receipts.length > 0 && (
        <>
          <div className={styles.infoRow}>
            <div className={styles.readByHeader}>
              <span className={styles.doubleCheckmarkIcon}></span>
              <strong>{tx('read_by')}</strong>
            </div>
          </div>
          {receipts
            .filter(
              (
                receipt: T.MessageReadReceipt
              ): receipt is T.MessageReadReceipt =>
                receipt.timestamp !== undefined
            )
            .map((receipt: T.MessageReadReceipt) => (
              <ReadReceiptFormatted
                key={receipt.contactId}
                receipt={receipt}
                onAction={props.onClose}
              />
            ))}
        </>
      )}
      {hasAttachment && (
        <>
          <div className={styles.infoRow}>
            <div className={styles.readByHeader}>
              <strong>{tx('attachment')}</strong>
            </div>
          </div>
          {message.fileName && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{tx('file')}</span>
              <span className={styles.infoValue}>{message.fileName}</span>
            </div>
          )}
          {message.fileMime && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>
                {tx('message_info_file_type')}
              </span>
              <span className={styles.infoValue}>{message.fileMime}</span>
            </div>
          )}
          {message.fileBytes > 0 && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>{tx('app_size')}</span>
              <span className={styles.infoValue}>
                {filesize(message.fileBytes)}
              </span>
            </div>
          )}
          {showDimensions && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>
                {tx('message_info_resolution')}
              </span>
              <span className={styles.infoValue}>
                {message.dimensionsWidth} × {message.dimensionsHeight} px
              </span>
            </div>
          )}
          {showDuration && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>
                {tx('message_info_duration')}
              </span>
              <span className={styles.infoValue}>
                {formatDuration(message.duration)}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function ReadReceiptFormatted(props: {
  receipt: T.MessageReadReceipt
  onAction?: () => void
}) {
  const accountId = selectedAccountId()
  const [contact, setContact] = useState<T.Contact | null>(null)
  const openViewProfileDialog = useOpenViewProfileDialog({
    onAction: props.onAction,
  })

  useEffect(() => {
    BackendRemote.rpc
      .getContact(accountId, props.receipt.contactId)
      .then(setContact)
  }, [props.receipt.contactId, accountId])

  const time = moment(props.receipt.timestamp * 1000)
  const timeFormatted = time.format('lll')

  if (!contact) {
    return null
  }

  return (
    <button
      type='button'
      className={styles.readReceiptFormatted}
      onClick={() => openViewProfileDialog(accountId, props.receipt.contactId)}
    >
      <div className={styles.readReceiptLeft}>
        <Avatar
          small
          displayName={contact.displayName}
          avatarPath={contact.profileImage}
          addr={contact.address}
          color={contact.color}
          aria-hidden={true}
        />
        <span className={styles.contactName}>{contact.displayName}</span>
      </div>
      <span className={styles.infoValue}>{timeFormatted}</span>
    </button>
  )
}
